import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  ClipboardCheck, 
  CheckCircle2, 
  Clock, 
  Circle,
  Leaf,
  Users,
  Building2,
  Save,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started", icon: Circle, color: "text-gray-500" },
  { value: "in_progress", label: "In Progress", icon: Clock, color: "text-blue-500" },
  { value: "completed", label: "Completed", icon: CheckCircle2, color: "text-emerald-500" },
];

const CATEGORY_ICONS = {
  Environmental: Leaf,
  Social: Users,
  Governance: Building2,
};

const CATEGORY_COLORS = {
  Environmental: "border-l-emerald-500 bg-emerald-50/30",
  Social: "border-l-blue-500 bg-blue-50/30",
  Governance: "border-l-purple-500 bg-purple-50/30",
};

export default function CSRDCompliance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editedItems, setEditedItems] = useState({});

  useEffect(() => {
    fetchChecklist();
  }, []);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/csrd`);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to fetch CSRD checklist");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id) => {
    const updates = editedItems[id];
    if (!updates) return;

    setSavingId(id);
    try {
      await axios.put(`${API}/csrd/${id}`, updates);
      toast.success("Item updated");
      fetchChecklist();
      setEditedItems((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (error) {
      toast.error("Failed to update item");
    } finally {
      setSavingId(null);
    }
  };

  const updateItemField = (id, field, value) => {
    setEditedItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const getItemValue = (item, field) => {
    return editedItems[item.id]?.[field] ?? item[field];
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Calculate progress
  const totalItems = items.length;
  const completedItems = items.filter((i) => i.status === "completed").length;
  const inProgressItems = items.filter((i) => i.status === "in_progress").length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const getStatusIcon = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status);
    return option ? option.icon : Circle;
  };

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status);
    return option ? option.color : "text-gray-500";
  };

  return (
    <div className="space-y-8" data-testid="csrd-compliance-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
          CSRD Compliance
        </h1>
        <p className="text-[#5c6b63] mt-2">
          Track your Corporate Sustainability Reporting Directive requirements
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="md:col-span-2 kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-[#5c6b63]">Overall Compliance</p>
                <p className="text-4xl font-bold text-[#1a2e22] font-['Outfit']">
                  {progressPercent}%
                </p>
              </div>
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <ClipboardCheck className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <Progress value={progressPercent} className="h-3" />
            <p className="text-sm text-[#5c6b63] mt-2">
              {completedItems} of {totalItems} requirements completed
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
                  {completedItems}
                </p>
                <p className="text-sm text-[#5c6b63]">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
                  {inProgressItems}
                </p>
                <p className="text-sm text-[#5c6b63]">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Checklist by Category */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, categoryItems]) => {
            const CategoryIcon = CATEGORY_ICONS[category] || ClipboardCheck;
            const categoryCompleted = categoryItems.filter(
              (i) => i.status === "completed"
            ).length;

            return (
              <Card key={category} className={`border-l-4 ${CATEGORY_COLORS[category]}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold text-[#1a2e22] font-['Outfit']">
                      <CategoryIcon className="w-5 h-5" />
                      {category}
                    </CardTitle>
                    <span className="text-sm text-[#5c6b63]">
                      {categoryCompleted}/{categoryItems.length} completed
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="space-y-2">
                    {categoryItems.map((item) => {
                      const StatusIcon = getStatusIcon(getItemValue(item, "status"));
                      const hasChanges = editedItems[item.id];

                      return (
                        <AccordionItem
                          key={item.id}
                          value={item.id}
                          className="border border-[#e0e7e3] rounded-xl px-4 data-[state=open]:bg-white"
                          data-testid={`csrd-item-${item.id}`}
                        >
                          <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center gap-3 text-left">
                              <StatusIcon
                                className={`w-5 h-5 ${getStatusColor(
                                  getItemValue(item, "status")
                                )}`}
                              />
                              <div>
                                <p className="font-medium text-[#1a2e22]">
                                  {item.requirement}
                                </p>
                                <p className="text-sm text-[#5c6b63]">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pt-2 pb-4">
                            <div className="space-y-4 pl-8">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-sm font-medium text-[#1a2e22]">
                                    Status
                                  </label>
                                  <Select
                                    value={getItemValue(item, "status")}
                                    onValueChange={(value) =>
                                      updateItemField(item.id, "status", value)
                                    }
                                  >
                                    <SelectTrigger data-testid={`status-select-${item.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {STATUS_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                          <div className="flex items-center gap-2">
                                            <option.icon
                                              className={`w-4 h-4 ${option.color}`}
                                            />
                                            {option.label}
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <label className="text-sm font-medium text-[#1a2e22]">
                                    Due Date
                                  </label>
                                  <Input
                                    type="date"
                                    value={getItemValue(item, "due_date") || ""}
                                    onChange={(e) =>
                                      updateItemField(item.id, "due_date", e.target.value)
                                    }
                                    className="input-field"
                                    data-testid={`due-date-${item.id}`}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-[#1a2e22]">
                                  Notes
                                </label>
                                <Textarea
                                  value={getItemValue(item, "notes") || ""}
                                  onChange={(e) =>
                                    updateItemField(item.id, "notes", e.target.value)
                                  }
                                  placeholder="Add notes about this requirement..."
                                  className="input-field min-h-[80px]"
                                  data-testid={`notes-${item.id}`}
                                />
                              </div>
                              {hasChanges && (
                                <Button
                                  onClick={() => handleUpdate(item.id)}
                                  disabled={savingId === item.id}
                                  className="btn-accent flex items-center gap-2"
                                  data-testid={`save-btn-${item.id}`}
                                >
                                  {savingId === item.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Save className="w-4 h-4" />
                                      Save Changes
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
